"use client"

import { createContext, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { getGeneralSettings } from "@/lib/settings-api"
import { clinicDateTimePreferences, type ClinicDateTimePreferences } from "@/lib/clinic-date-time"
import { hasFlag } from "@/lib/feature-flags"
import { useAuthStore } from "@/stores/auth-store"

export const ClinicDateTimeContext = createContext<ClinicDateTimePreferences>({})

export function ClinicDateTimeProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  // Off the flag, staff views keep the language's own date and time formats.
  const applies = useAuthStore((state) => hasFlag(state.user?.feature_flags, "clinic_date_formats_enabled"))
  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: getGeneralSettings,
    enabled: isAuthenticated && applies,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    // One shared poll lets other active sessions pick up an admin's changes.
    refetchInterval: 60 * 1000,
  })
  const preferences = useMemo(
    () => clinicDateTimePreferences(isAuthenticated && applies ? data : undefined),
    [data, isAuthenticated, applies],
  )

  return <ClinicDateTimeContext.Provider value={preferences}>{children}</ClinicDateTimeContext.Provider>
}
